/* eslint-disable no-console */
/* eslint-disable consistent-return */
import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import FormularioService from '../API/FormularioService';
import { Footer } from '../components/Footer';
import { showAlert } from '../redux/global/globalSlice';
import { FileIcon } from '../components/atoms/FileIcon';
import { DenunciaStepper } from '../components/molecules/DenunciaStepper';
import { setEstatus } from '../redux/catalog/catalogSlice';
import { DenunciaComments } from '../components/molecules/DenunciaComments';
import analyticsService from '../API/analyticsService';

export const ConsultaDenunciaScreen = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyles();

  const [denunciaData, setDenunciaData] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const getCatalogs = useCallback(async () => {
    try {
      const [statusResp] = await Promise.all([FormularioService.catalogStatus({ dispatch })]);
      setEstatus(dispatch, statusResp);
    } catch (error) {
      console.error('Error fetching catalogs:', error);
    }
  }, []);

  const handleUpdateComments = useCallback(newComment => {
    setDenunciaData(prevData => ({
      ...prevData,
      comentarios: prevData.comentarios?.map(comment => {
        if (comment.idComentario === newComment.idComentario) {
          return newComment;
        }
        return comment;
      }),
    }));
  }, []);

  useEffect(() => {
    if (!location.state?.folio || !location.state?.password) {
      showAlert(dispatch, {
        type: 'ERROR',
        message: 'No se proporcionaron los datos necesarios para consultar la denuncia.',
      });
      return history.replace('/');
    }

    const getDenunciaData = async () => {
      try {
        const resp = await FormularioService.consultaFolio({
          dispatch,
          folio: location.state.folio,
          clave: location.state.password,
          showLoader: true,
        });

        analyticsService.sendEvent('SEGUIMIENTO_DENUNCIA');
        await getCatalogs();
        setDenunciaData(resp);
      } catch (error) {
        console.error('Error fetching denuncia data:', error);
        history.replace('/');
      }
    };

    getDenunciaData();
  }, [location?.state?.folio, location?.state?.password]);

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h5" component="h1" className={classes.headerTitle}>
          Da seguimiento a tu denuncia
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '1fr 1fr',
          },
          gap: 24,
        }}
      >
        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>
            <Typography className={classes.sectionHeaderTitle}>Denuncia</Typography>
          </Box>
          <Box className={classes.sectionContent}>
            <Box
              sx={{
                display: 'flex',
                gap: 10,
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="body1" className={classes.bold}>
                Folio:{' '}
                <Typography
                  variant="body1"
                  component="span"
                  className={`${classes.bold} ${classes.textValue}`}
                  color="primary"
                >
                  {denunciaData?.folio}
                </Typography>
              </Typography>
              {denunciaData?.fechaCreacion && (
                <Typography variant="body1" className={classes.bold}>
                  Fecha de la denuncia:{' '}
                  <Typography variant="body1" component="span" className={`${classes.textValue}`}>
                    {Intl.DateTimeFormat('es-MX', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    }).format(new Date(denunciaData?.fechaCreacion))}
                  </Typography>
                </Typography>
              )}
            </Box>
            <Divider style={{ marginTop: '5px' }} />
            <Box
              sx={{
                marginTop: 20,
              }}
            >
              <Box
                sx={{
                  mb: 2,
                }}
              >
                <Box className={classes.textWrapContainer}>
                  <Typography variant="body1" className={classes.bold}>
                    Tipo de denuncia:{' '}
                    <Typography variant="body1" component="span" className={`${classes.textValue}`}>
                      {denunciaData?.usrAnonimo ? 'Anónima' : 'No anónima'}
                    </Typography>
                  </Typography>
                  {!denunciaData?.usrAnonimo && denunciaData?.tipoUsuario && (
                    <Typography variant="body1" className={classes.bold}>
                      Soy:{' '}
                      <Typography variant="body1" component="span" className={`${classes.textValue}`}>
                        {denunciaData.tipoUsuario}
                      </Typography>
                    </Typography>
                  )}
                </Box>
                {!denunciaData?.usrAnonimo && denunciaData?.usrNombre && (
                  <Typography variant="body1" className={classes.bold}>
                    Nombre(s) y apellidos:{' '}
                    <Typography variant="body1" component="span" className={`${classes.textValue}`}>
                      {denunciaData?.usrNombre}
                    </Typography>
                  </Typography>
                )}
                {!denunciaData?.usrAnonimo && (
                  <Box className={classes.textWrapContainer}>
                    {denunciaData?.usrTelefono && (
                      <Typography variant="body1" className={classes.bold}>
                        Teléfono:{' '}
                        <Typography variant="body1" component="span" className={`${classes.textValue}`}>
                          {denunciaData?.usrTelefono}
                        </Typography>
                      </Typography>
                    )}
                    {denunciaData?.usrCorreo && (
                      <Typography variant="body1" className={classes.bold}>
                        Correo electrónico:{' '}
                        <Typography variant="body1" component="span" className={`${classes.textValue}`}>
                          {denunciaData.usrCorreo}
                        </Typography>
                      </Typography>
                    )}
                  </Box>
                )}
                {!denunciaData?.usrAnonimo && denunciaData?.genero && (
                  <Typography variant="body1" className={classes.bold}>
                    Género:{' '}
                    <Typography variant="body1" component="span" className={`${classes.textValue}`}>
                      {denunciaData.genero}
                    </Typography>
                  </Typography>
                )}
              </Box>
              <Typography variant="body1" className={classes.bold}>
                Descripción
              </Typography>
              <Box className={classes.descriptionContainer}>
                <Typography variant="body1" className={classes.descriptionText}>
                  {denunciaData?.txtSituacion && denunciaData.txtSituacion.length > 900
                    ? denunciaData.txtSituacion.substring(0, 900)
                    : denunciaData?.txtSituacion}
                </Typography>
                {denunciaData?.txtSituacion && denunciaData.txtSituacion.length > 900 && (
                  <Box className={classes.fadeOverlay} />
                )}
              </Box>
              {denunciaData?.txtSituacion && denunciaData.txtSituacion.length > 900 && (
                <Button
                  variant="text"
                  color="primary"
                  className={classes.seetTextButton}
                  onClick={() => setShowFullDescription(true)}
                >
                  Ver descripción completa
                </Button>
              )}
            </Box>

            <Box sx={{ marginTop: 20 }}>
              <Typography variant="body1" className={classes.bold} gutterBottom>
                Archivos adjuntos
              </Typography>

              {denunciaData?.listaArchivos && denunciaData.listaArchivos.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 5,
                    maxHeight: 93,
                    overflowY: 'auto',
                    height: 93,
                  }}
                >
                  {denunciaData.listaArchivos.map((archivo, index) => (
                    <Box
                      key={`file-${index}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 4,
                        borderRadius: 4,
                        padding: '2px 8px',
                        border: '1px solid #D8DCDF',
                      }}
                    >
                      <Typography variant="body1" className={`${classes.textValue} ${classes.bold}`}>
                        {index + 1}.
                      </Typography>
                      <FileIcon color="#000" />
                      <Typography variant="body1" className={classes.textValue}>
                        {archivo.nombreArchivo}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
            height: '100%',
          }}
        >
          <Box className={classes.section}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionHeaderTitle}>Estatus de la denuncia</Typography>
            </Box>
            <Box>
              <DenunciaStepper idEstatusDenuncia={denunciaData?.idEstatusDenuncia} />
            </Box>
          </Box>
          <Box className={classes.section} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionHeaderTitle}>Buzón de notificaciones</Typography>
            </Box>
            <Box className={classes.sectionContent} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <DenunciaComments
                comments={denunciaData?.comentarios || []}
                denunciaNumber={location.state?.folio}
                password={location.state?.password}
                fechaCreacion={denunciaData?.fechaCreacion}
                onUpdateComment={handleUpdateComments}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box className={classes.actionsFooter}>
        <Footer />
        <Box className={classes.actionsButtons}>
          <Link to="/">
            <Button variant="outlined" color="primary" style={{ backgroundColor: '#fff' }}>
              Regresar
            </Button>
          </Link>
        </Box>
      </Box>

      <Dialog
        open={showFullDescription}
        aria-labelledby="consult-dialog-title"
        aria-describedby="consult-dialog-description"
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        }}
        scroll="paper"
        maxWidth="md"
        PaperProps={{
          style: {
            borderRadius: 16,
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
            padding: '24px'
          },
        }}
      >
        <DialogContent className={classes.descriptionDialogContent}>
          <Typography variant="body1" className={classes.dialogDescriptionTitle} gutterBottom>
            Descripción
          </Typography>
          <Typography variant="body1" className={classes.descriptionDialogText}>
            {denunciaData?.txtSituacion}
          </Typography>
        </DialogContent>
        <DialogActions className={classes.dialogDescriptionActions}>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowFullDescription(false)}
              disableElevation
              style={{ minWidth: 102 }}
            >
              Cerrar
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  header: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, '16px'),
    borderRadius: 4,
  },
  headerTitle: {
    fontWeight: 'bold',
    textAlign: 'left',
  },
  section: {
    borderRadius: 8,
    backgroundColor: '#fff',
    boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
    overflow: 'hidden',
    boxSizing: 'border-box',
    position: 'relative',
  },
  sectionHeader: {
    color: '#fff',
    backgroundColor: '#273138',
    padding: theme.spacing('8px', '16px'),
  },
  sectionHeaderTitle: {
    fontWeight: '600',
    fontSize: '1rem',
  },
  bold: {
    fontWeight: '600',
  },
  sectionContent: {
    padding: theme.spacing('16px', '24px'),
    position: 'relative',
  },
  actionsFooter: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap-reverse',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
    },
  },
  actionsButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-end',
    },
  },
  textValue: {
    fontSize: '0.9rem',
  },
  descriptionText: {
    fontSize: '0.9rem',
    whiteSpace: 'pre-line',
    wordWrap: 'break-word',
  },
  descriptionContainer: {
    position: 'relative',
    maxHeight: 'none',
  },
  fadeOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '60px',
    background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%)',
    pointerEvents: 'none',
  },
  descriptionDialogContent: {
    padding: '0px',
  },
  descriptionDialogText: {
    fontSize: '14px',
    whiteSpace: 'pre-line',
    wordWrap: 'break-word',
    color: '#232322',
    lineHeight: '16px',
    fontWeight: '400',
  },
  textWrapContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(0),
    },
  },
  seetTextButton: {
    marginTop: '8px',
    textTransform: 'none',
    textDecoration: 'underline',
    transition: 'all 0.3s ease',
    '&:hover': {
      color: '#D05319',
      textDecoration: 'underline',
      backgroundColor: 'transparent',
    },
  },
  dialogDescriptionTitle: {
    fontWeight: '600',
    marginBottom: '12px',
    color: '#232322',
    lineHeight: '20px',
    letterSpacing: '0.5px',
  },
  dialogDescriptionActions: {
    marginTop: '16px',
    padding: '0px',
  },
}));
